<template>
  <div class="mt-n10 pt-0">
    <v-navigation-drawer
      v-model="drawer"      
      app
      right
      width="550"
      temporary
      hide-overlay
    >
      <v-toolbar
        color="hrissecondary" 
        dark
      >
        <v-toolbar-title class="white--text">
          Advanced Filter
        </v-toolbar-title>
      </v-toolbar>
      <v-container> 
        <v-row no-gutters>
          <v-col cols="12">
            <v-autocomplete
              v-model="statusFilterIds"
              :items="approvalStatusTypes"              
              label="Status"
              placeholder="Approval Status"
              item-text="name"
              item-value="id"
              hide-no-data
              hide-selected
              chips
              multiple
              deletable-chips
              clearable
            />
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedCompanies"
              :items="companies"              
              label="Company"
              item-text="name"
              item-value="id"
              hide-no-data
              hide-selected
              chips
              multiple
              deletable-chips
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            class="mt-8"
          >
            <v-menu
              v-model="dateRangeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateRangeDisplay"
                  label="Submission Date"  
                  readonly
                  class="label-position"
                  prepend-icon="mdi-calendar"
                  placeholder="Select the date range for submission date"
                  clearable
                  v-on="on"
                  @click:clear="dateRange = []"
                />
              </template>
              <v-date-picker
                v-model="dateRange"
                :max="today"
                range
                no-title
                :allowed-dates="allowedDates"
              />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            class="text-center mb-5 pr-5"
            style="position: fixed; bottom: 0;"
          >
            <v-btn
              block
              color="hrissecondary"
              class="white--text"
              @click="loadClaimApprovals"
            >
              Search
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-navigation-drawer>
    <v-container
      class="hidden-sm-and-down"
      fluid
    >
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-toolbar
              flat
              color="white"
            >
              <v-text-field
                ref="searchInput"
                v-model="searchInput"
                autofocus
                append-icon="search"
                placeholder="Search by name or reference number"
                single-line
                hide-details
                clearable
              />
              <v-spacer />
              <v-tooltip
                content-class="bottom"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn   
                    text
                    icon           
                    color="primary"
                    v-on="on"
                    @click.stop="drawer = true"
                  >
                    <v-icon 
                      v-if="isFiltering" 
                      medium
                    >
                      filter_list_alt
                    </v-icon>
                    <v-icon 
                      v-else
                      medium
                    >
                      filter_list
                    </v-icon>
                  </v-btn> 
                </template>
                <span>Advanced filter</span>
              </v-tooltip>
            
              <v-tooltip
                content-class="bottom"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-avatar 
                    class="cursor-pointer"
                    v-on="on"
                    @click="loadClaimApprovals"                
                  >
                    <v-icon medium>
                      mdi-refresh
                    </v-icon>
                  </v-avatar>
                </template>
                <span>Reload</span>
              </v-tooltip>
            </v-toolbar>

            <v-data-table
              :loading="tableLoading"
              :items="claimRequests"
              :headers="filteredHeaders"
              :single-expand="true"
              :items-per-page="-1"
              show-expand
              hide-default-footer
              fixed-header
              class="claim-approval-listing"
              height="600px"
            > 
              <template v-slot:item.detail="{ item }">
                <v-row 
                  class="ml-0"
                >
                  <v-col 
                    :cols="$vuetify.breakpoint.smAndDown ? 12 : 2"
                    xl="2"
                    lg="4"
                    md="6"
                    sm="12"
                  >
                    <v-avatar
                      color="teal"
                      size="40"
                      class="mr-2"
                    >
                      <img
                        v-if="item.employee.thumbnail_url"
                        :src="item.employee.thumbnail_url" 
                        alt="avatar"
                      >
                      <span 
                        v-else
                        class="white--text headline"
                      >{{ item.employee.person.name.match(/\b\w/g).slice(0, 1).join('') }}
                      </span>
                    </v-avatar>
                  </v-col>

                  <v-col>
                    <v-row class="font-weight-bold">
                      {{ (item.employee.person || {}).name }}
                    </v-row>
                    <v-row>
                      {{ (item.claim_request_entries || {}).length + ' item ' }}
                      <v-icon color="hrissecondary">
                        mdi-circle-small
                      </v-icon>
                      {{ 'Created at ' + $moment(item.created_at).format('YYYY-MM-DD hh:mm A') }}
                    </v-row>
                    <v-row class="red--text text--lighten-1 caption">
                      {{ item.claim_request_escalation ? 'Escalate to ' + (((item.claim_request_escalation || {}).employee || {}).person || {}).name : '' }}
                    </v-row>
                  </v-col>
                </v-row>
              </template>

              <template v-slot:item.company="{ item }">
                <div>
                  <span>
                    {{ ((item.company_employee || {}).company || {}).name }}
                  </span>
                </div>
              </template>

              <template v-slot:item.cycle="{ item }">
                <div>
                  <span>
                    {{ item.claim_cycle.start_date + ' - ' + item.claim_cycle.end_date }}
                  </span>
                </div>
              </template>

              <template v-slot:item.total_amount="{ item }">
                <div>
                  <span
                    class="font-weight-bold"
                  >
                    {{ thousandSeparator((item.total_request_amount / 100).toFixed(2)) }}
                  </span>
                </div>
              </template>

              <template v-slot:item.date="{ item }">
                <div>
                  <span>
                    {{ $moment(item.request_date).format('DD MMM YYYY') }}
                  </span>
                </div>
              </template>

              <template v-slot:item.status="{ item }">
                <span
                  :class="{ 
                    'warning--text': (
                      item.approval_request.latest_approval.approval_status_type_id === 100 || 
                      item.approval_request.latest_approval.approval_status_type_id === 201 || 
                      item.approval_request.latest_approval.approval_status_type_id === 250 || 
                      item.approval_request.latest_approval.approval_status_type_id === 260 || 
                      item.approval_request.latest_approval.approval_status_type_id === 270 || 
                      item.approval_request.latest_approval.approval_status_type_id === 400
                    ),
                    'red--text': (
                      item.approval_request.latest_approval.approval_status_type_id === 500 ||
                      item.approval_request.latest_approval.approval_status_type_id === 502 ||
                      item.approval_request.latest_approval.approval_status_type_id === 300
                    ),
                    'grey--text': (item.approval_request.latest_approval.approval_status_type_id === 501),
                    'green--text': (
                      item.approval_request.latest_approval.approval_status_type_id === 200 || 
                      item.approval_request.latest_approval.approval_status_type_id === 600
                    ),
                  }"
                >
                  {{ item.approval_request.latest_approval.status.name }}
                </span>
              </template>
                
              <template v-slot:item.action="{ item }">
                <div>
                  <employee-claims-approvals-dialog-list-histories
                    :claim-request="item"
                    :requester="item.employee.person"
                  />
                  <employee-claims-approvals-dialog-edit
                    v-if="$hasPermissionTo(['update claim approvals']) && isPendingApproval(item.approval_request.latest_approval.approval_status_type_id)"
                    :is-approve-on-behalf="isApproveOnBehalf"
                    :is-assigned-to-me="isAssignedToMe"
                    :claim-request="item"
                    @updated="loadClaimApprovals"
                  />
                </div>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td
                  :colspan="headers.length"
                  class="px-0"
                >        
                  <v-container>          
                    <v-row>
                      <v-col cols="12">
                        <v-card class="elevation-5">
                          <v-data-table
                            :headers="entryHeaders"
                            :items="item.claim_request_entries"
                            hide-default-footer
                            disable-pagination
                          >
                            <template v-slot:item.claim_type="{ item }">
                              <span>
                                {{ item.claim_type.name }} {{ item.entitlement_date_to_deduct ? '(' + $moment(item.entitlement_date_to_deduct).format('MMM YYYY') + ')' : '' }}
                              </span>
                            </template>
                            <template v-slot:item.receipt_date="{ item }">
                              <span>
                                {{ item.receipt_date ? $moment(item.receipt_date).format('DD MMM YYYY') : '-' }}
                              </span>
                            </template>
                            <template v-slot:item.receipt_number="{ item }">
                              <span>
                                {{ item.receipt_number ? item.receipt_number : '-' }}
                              </span>
                            </template>
                            <template v-slot:item.amount="{ item }">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    color="hrissecondary"
                                    v-on="on"
                                  >
                                    mdi-information-outline
                                  </v-icon>
                                </template>
                                RM{{ thousandSeparator((item.amount / 100).toFixed(2)) }} x {{ item.quantity }} unit
                              </v-tooltip>
                              <span>
                                {{ thousandSeparator((item.total_amount / 100).toFixed(2)) }}
                              </span>
                            </template>
                            <template v-slot:item.view="{ item }">
                              <employee-claims-approvals-entries-dialog-view :claim-request-entry="item" />
                            </template>
                          </v-data-table>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-container>
                </td>
              </template>
            </v-data-table>
            <v-divider class="ml-0 mr-0" />
            <v-container class="text-right">
              <v-btn
                icon
                color="primary"
                :disabled="currentPage === 1"
                @click="getPage(currentPage - 1)"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <span class="pagination">{{ from }}-{{ to }} of {{ total }}</span>
              <v-btn
                icon
                color="primary"
                :disabled="currentPage === lastPage"
                @click="getPage(currentPage + 1)"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-container> 
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  export default {
    components: {
      EmployeeClaimsApprovalsEntriesDialogView: () => import('@/components/employee/claims/approvals/entries/DialogView'),
      EmployeeClaimsApprovalsDialogEdit: () => import('@/components/employee/claims/approvals/DialogEdit'),
      EmployeeClaimsApprovalsDialogListHistories: () => import('@/components/employee/claims/approvals/DialogListHistories'),
    },
    data () {
      return {      
        headers: [
          {
            text: 'Employee & Claim Details',
            align: 'left',
            value: 'detail',
            sortable: false,
          },
          {
            text: 'Company',
            align: 'left',
            value: 'company',
            sortable: false,
          },
          {
            text: 'Claim Cycle',
            align: 'left',
            value: 'cycle',
            sortable: false,
          },
          {
            text: 'Reference No.',
            align: 'center',
            value: 'reference_number',
            sortable: false,
          },
          {
            text: 'Total (RM)',
            align: 'right',
            value: 'total_amount',
            sortable: false,
          },
          {
            text: 'Request Date',
            align: 'center',
            value: 'date',
            sortable: false,
          },
          {
            text: 'Status',
            align: 'left',
            value: 'status',
            width: '95px',
            sortable: false,
          },
        ],
        entryHeaders: [
          {
            text: 'Claim Type',
            align: 'left',
            value: 'claim_type',
            sortable: false,
          },
          {
            text: 'Receipt / Transaction Date',
            align: 'center',
            value: 'receipt_date',
            sortable: false,
          },
          {
            text: 'Receipt No.',
            align: 'center',
            value: 'receipt_number',
            sortable: false,
          },
          {
            text: 'Amount (RM)',
            align: 'right',
            value: 'amount',
            sortable: false,
          },
          {
            text: '',
            align: 'center',
            value: 'view',
            sortable: false,
          },
        ],
        searchInput: '',
        tableLoading: false,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 0, 
        currentPage: 1,
        claimRequests: [],
        drawer: null,
        statusFilterIds: [],
        approvalStatusTypes: [],
        isApproveOnBehalf: true,
        dateRangeMenu: false,
        dateRange: [],
        today: new Date().toISOString().slice(0, 10), 
        claimTransactionDateValidityRules: [],
        approvalWorkflowBatches: [],
        isAssignedToMe: true,
        companies: [],
        selectedCompanies: [],
      }
    },
    computed: {
      ...mapState(['currentEmployee']),
      filteredHeaders () {
        return this.canUpdateClaimApproval() ? [...this.headers, {
          text: '',
          align: 'left',
          value: 'action',
          sortable: false,
        },
        ] : this.headers
      },
      isFiltering () {
        return !_.isEmpty(this.statusFilterIds)
      },
      dateRangeDisplay () {
        if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
          const [start, end] = this.dateRange
          const startFormatted = this.$moment(start).format('DD MMM YYYY')
          const endFormatted = this.$moment(end).format('DD MMM YYYY')

          return `${startFormatted} - ${endFormatted}`
        } else {
          return ''
        }
      },
    },
    watch: {
      searchInput (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.tableLoading = true
          this.loadClaimApprovals()
        }, 500) /* 500ms throttle */
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    created () {
      this.loadData()
      this.loadApprovalStatus()
      this.loadClaimApprovalWorkflowBatches()
      this.statusFilterIds = [100, 201, 260, 270, 502]
    },
    mounted () {
      this.loadClaimApprovals()
    },
    methods: {
      loadApprovalStatus () {
        this.$api.approvalStatus().list()
          .then(response => {
            let statusType = response.data.filter(item => {
              return item.id === 100 || item.id === 201 || item.id === 260 || item.id === 270 || item.id === 502
            })
            this.approvalStatusTypes = _.orderBy(statusType, ['id'], ['asc'])
            this.statusFilterIds = [100, 201, 260, 270, 502]
          })
      },
      loadClaimApprovalWorkflowBatches () {
        this.$api.claimApprovalWorkflowBatches().list()
          .then(response => {
            this.approvalWorkflowBatches = _.orderBy(response.data, ['start_date'], ['desc'])
          })
      },
      loadData () {
        Promise.all([
          this.$api.hrisCompanies().list('?organization=' + process.env.VUE_APP_ORG),
          this.$api.claimTransactionDateValidityRules().list(),
        ])
          .then(([companies, rules]) => {
            this.claimTransactionDateValidityRules = rules.data
            this.companies = _.orderBy(companies.data, ['name'], ['asc'])
            this.companies.unshift({ id: 'ALL', name: 'All Companies' })
          })
      },
      canUpdateClaimApproval () {
        return this.$hasPermissionTo(['update claim approvals'])
      },
      isPendingApproval (status) {
        if (status === 100 || status === 201 || status === 260 || status === 270 || status === 502) {
          return true
        } else {
          return false
        }
      },
      loadClaimApprovals () {
        this.tableLoading = true
        
        this.$api.claimApprovals().list('', this.prepareFormData())
          .then(response => {
            this.setPaginationDetails(response.data)
            this.setClaimRequestData(response.data.data)

            this.tableLoading = false
          })
      },
      prepareFormData () {
        return {
          isApproveOnBehalf: this.isApproveOnBehalf,
          isAssignedToMe: this.isAssignedToMe,
          searchInput: this.searchInput,
          status: this.statusFilterIds,
          isIncludeSubordinate: 1,
          isIncludeNonSubordinate: 1,
          dateRange: this.getAllDatesInRange(),
          companies: this.selectedCompanies.includes('ALL') ? (
            this.companies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies,
        }
      },
      getAllDatesInRange () {
        const [start, end] = this.dateRange
        if (!start || !end) return []

        const startDate = this.$moment(start)
        const endDate = this.$moment(end)
        const dateList = []

        while (startDate.isSameOrBefore(endDate)) {
          dateList.push(startDate.format('YYYY-MM-DD'))
          startDate.add(1, 'day')
        }

        return dateList
      },
      getPage (page) {
        this.tableLoading = true

        this.$api.claimApprovals().list('?page=' + page, this.prepareFormData())
          .then(response => {
            this.setPaginationDetails(response.data)
            this.setClaimRequestData(response.data.data)
            
            this.tableLoading = false
          })
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      setPaginationDetails (page) {
        this.total = page.total
        this.from = page.from
        this.to = page.to
        this.lastPage = page.last_page
        this.currentPage = page.current_page
      },
      setClaimRequestData (data) {
        this.claimRequests = _.orderBy(data.map(request => {
          const requestDate = this.$moment(request.request_date, 'YYYY-MM-DD')
          const maxEndDay = this.claimTransactionDateValidityRules[this.claimTransactionDateValidityRules.length - 1].end_day

          let matchedCompanyEmployees = request.employee.company_employees.filter(ce => {
            const startDate = this.$moment(ce.start_date, 'YYYY-MM-DD')
            const endDate = ce.end_date ? this.$moment(ce.end_date, 'YYYY-MM-DD') : null
            const cutoffDate = this.$moment(requestDate).subtract(maxEndDay, 'days')

            return (
              requestDate.isSameOrAfter(startDate) &&
              (!endDate || requestDate.isSameOrBefore(endDate) || endDate.isSameOrAfter(cutoffDate))
            )
          })

          let batch = this.approvalWorkflowBatches.find(batch => {
            const startDate = this.$moment(batch.start_date, 'YYYY-MM-DD')
            const endDate = batch.end_date ? this.$moment(batch.end_date, 'YYYY-MM-DD') : null

            return (
              requestDate.isSameOrAfter(startDate) &&
              (!endDate || requestDate.isSameOrBefore(endDate))
            )
          })
          
          let filteredCompanyEmployees = []

          if (request.claim_request_company) {
            filteredCompanyEmployees = matchedCompanyEmployees.filter(ce => ce.company_uid === request.claim_request_company.company_uid)
          } else {
            if (matchedCompanyEmployees.some(ce => ce.is_active_pay_status === 1)) {
              filteredCompanyEmployees = matchedCompanyEmployees.filter(ce => ce.is_active_pay_status === 1)
            }
          }

          let latestCompanyEmployee = _.maxBy(filteredCompanyEmployees, ce =>
            this.$moment(ce.created_at, 'YYYY-MM-DD').valueOf() // backend is latest company employee, so cannot use start_date
          )

          if (!latestCompanyEmployee) {
            latestCompanyEmployee = matchedCompanyEmployees[0]
          }

          return {
            ...request,
            claim_approval_workflow_batch: batch,
            company_employee: latestCompanyEmployee,
          }
        }), ['reference_number'], ['desc'])
      },
      allowedDates (date) {
        // if only the first date (start) is selected
        if (this.dateRange.length === 1) {
          const start = this.dateRange[0]
          // disable any date *before* the start
          return date >= start
        }
        // otherwise allow all dates (default)
        return true
      },
    },
  }
</script>

<style scoped>
.claim-approval-listing >>> ::-webkit-scrollbar {
  width: 3px !important; 
}

/* Track */
.claim-approval-listing >>> ::-webkit-scrollbar-track {
  box-shadow: inset 0 0 2px #e6e6e6; 
  border-radius: 10px;
}
 
/* Handle */
.claim-approval-listing >>> ::-webkit-scrollbar-thumb {
  background: grey; 
  border-radius: 10px;
  height: 2px;
}

/* Handle on hover */
.claim-approval-listing >>> ::-webkit-scrollbar-thumb:hover {
  background:  #555; 
}

.pagination{
  font-size:12px;
  color:#5F6368
}

::v-deep .v-toolbar__content{
  margin-left:0px !important;
}

.cursor-pointer{
  cursor: pointer;
} 
</style>
